import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

import { ProjectTaskService } from 'src/app/private/services/project-task.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';

const EMPTY_FORM = () => ({
  task_item_id: null as any,

  work_date: '',

  description: '',

  is_billable: true,

  total_minutes: null as number | null,

  start_time: '',

  end_time: '',

  hourly_rate: null as number | null,
});

@Component({
  selector: 'app-project-task-time-log',
  templateUrl: './project-task-time-log.component.html',
  styleUrls: ['./project-task-time-log.component.scss'],
})
export class ProjectTaskTimeLogComponent implements OnInit, OnDestroy {
  @Input() taskId!: any;
  @Input() projectId!: any;
  @Input() viewOnly = false;

  private destroy$ = new Subject<void>();

  // ── Data ─────────────────────────────────────────────────────────────────
  timeLogs: any[] = [];

  subtasks: any[] = [];

  loading = true;

  subtasksLoading = false;

  submitting = false;

  editSubmitting = false;

  // ── Add form ──────────────────────────────────────────────────────────────
  form = EMPTY_FORM();

  useTimeRange = false;

  // ── Edit state ────────────────────────────────────────────────────────────
  editingId: any = null;

  editForm: any = {};

  editUseTimeRange = false;

  // ── Delete state ──────────────────────────────────────────────────────────
  deletingId: any = null;

  // ── Current user ─────────────────────────────────────────────────────────
  currentUserId: any;

  currentUserName = '';

  constructor(
    private api: ProjectTaskService,
    private localStorage: LocalStorageService,
    private toastr: ToastrService,
  ) {}

  ngOnInit() {
    const u = this.localStorage.getUser();

    this.currentUserId = u.user_id;

    this.currentUserName = `${u.fname || ''} ${u.lname || ''}`.trim();

    this.loadTimeLogs();

    this.loadSubtasks();
  }

  ngOnDestroy() {
    this.destroy$.next();

    this.destroy$.complete();
  }

  // ── Load Time Logs ────────────────────────────────────────────────────────
  loadTimeLogs() {
    this.loading = true;

    this.api
      .getAllTaskTimeLogs({
        task_id: this.taskId,
        limit: 100,
        client_id: this.localStorage.getClientId(),
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.timeLogs = res?.rows ?? [];

          this.loading = false;
        },

        error: () => {
          this.loading = false;

          this.toastr.error('Failed to load time logs');
        },
      });
  }

  // ── Load Subtasks ─────────────────────────────────────────────────────────
  loadSubtasks() {
    this.subtasksLoading = true;

    this.api
      .getAllTaskItems({
        task_id: this.taskId,
        client_id: this.localStorage.getClientId(),
        limit: 100,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.subtasks = res?.rows ?? [];

          this.subtasksLoading = false;
        },

        error: () => {
          this.subtasksLoading = false;

          this.toastr.error('Failed to load subtasks');
        },
      });
  }

  // ── Add ───────────────────────────────────────────────────────────────────
  addTimeLog() {
    if (!this.canSubmit()) return;

    this.submitting = true;

    const payload: any = {
      task_id: this.taskId,

      project_id: this.projectId,

      user_id: this.currentUserId,

      work_date: this.form.work_date,

      description: this.form.description,

      is_billable: this.form.is_billable,

      client_id: this.localStorage.getClientId(),
    };

    if (this.form.task_item_id) {
      payload.task_item_id = this.form.task_item_id;
    }

    if (this.useTimeRange && this.form.start_time && this.form.end_time) {
      if (new Date(this.form.start_time) > new Date(this.form.end_time)) {
        this.toastr.error('Start time cannot be greater than End time');
        this.submitting = false;
        return;
      }

      payload.start_time = this.form.start_time;

      payload.end_time = this.form.end_time;
    } else {
      payload.total_minutes = Number(this.form.total_minutes);
    }

    if (this.form.hourly_rate) {
      payload.hourly_rate = this.form.hourly_rate;
    }

    this.api
      .createTaskTimeLog(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.submitting = false;

          this.form = EMPTY_FORM();

          this.useTimeRange = false;

          this.toastr.success('Time logged successfully');

          this.loadTimeLogs();
        },

        error: () => {
          this.submitting = false;

          this.toastr.error('Failed to log time');
        },
      });
  }

  canSubmit(): boolean {
    if (!this.form.work_date) return false;

    if (this.useTimeRange) {
      return !!(this.form.start_time && this.form.end_time);
    }

    return !!(this.form.total_minutes && Number(this.form.total_minutes) > 0);
  }

  resetForm() {
    this.form = EMPTY_FORM();
    this.useTimeRange = false;
  }

  // ── Edit ─────────────────────────────────────────────────────────────────
  startEdit(log: any) {
    this.editingId = log.time_log_id;

    this.editUseTimeRange = !!(log.start_time && log.end_time);

    this.editForm = {
      task_item_id: log.task_item_id || null,

      work_date: log.work_date ? log.work_date.substring(0, 10) : '',

      description: log.description || '',

      is_billable: log.is_billable,

      total_minutes: log.total_minutes,

      start_time: log.start_time ? this.toDatetimeLocal(log.start_time) : '',

      end_time: log.end_time ? this.toDatetimeLocal(log.end_time) : '',

      hourly_rate: log.hourly_rate || null,
    };

    this.deletingId = null;
  }

  cancelEdit() {
    this.editingId = null;

    this.editForm = {};
  }

  submitEdit() {
    if (!this.editForm.work_date) return;

    this.editSubmitting = true;

    const payload: any = {
      where: {
        time_log_id: this.editingId,
      },

      task_item_id: this.editForm.task_item_id || null,

      work_date: this.editForm.work_date,

      description: this.editForm.description,

      is_billable: this.editForm.is_billable,

      updated_by: this.currentUserId,

      client_id: this.localStorage.getClientId(),
    };

    if (
      this.editUseTimeRange &&
      this.editForm.start_time &&
      this.editForm.end_time
    ) {
      if (new Date(this.editForm.start_time) > new Date(this.editForm.end_time)) {
        this.toastr.error('Start time cannot be greater than End time');
        this.editSubmitting = false;
        return;
      }

      payload.start_time = this.editForm.start_time;

      payload.end_time = this.editForm.end_time;
    } else {
      payload.total_minutes = Number(this.editForm.total_minutes);
    }

    if (this.editForm.hourly_rate) {
      payload.hourly_rate = this.editForm.hourly_rate;
    }

    this.api
      .updateTaskTimeLog(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.editSubmitting = false;

          this.editingId = null;

          this.toastr.success('Time log updated');

          this.loadTimeLogs();
        },

        error: () => {
          this.editSubmitting = false;

          this.toastr.error('Failed to update time log');
        },
      });
  }

  // ── Delete ────────────────────────────────────────────────────────────────
  confirmDelete(id: any) {
    this.deletingId = id;

    this.editingId = null;
  }

  cancelDelete() {
    this.deletingId = null;
  }

  deleteTimeLog(id: any) {
    this.api
      .deleteTaskTimeLog({
        where: {
          time_log_id: id,
        },

        deleted_by: this.currentUserId,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.deletingId = null;

          this.toastr.success('Time log deleted');

          this.loadTimeLogs();
        },

        error: () => {
          this.deletingId = null;

          this.toastr.error('Failed to delete time log');
        },
      });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  isOwner(log: any): boolean {
    return String(log.user_id) === String(this.currentUserId);
  }

  formatMinutes(mins: number): string {
    if (!mins && mins !== 0) return '—';

    const h = Math.floor(mins / 60);

    const m = mins % 60;

    if (h === 0) return `${m}m`;

    if (m === 0) return `${h}h`;

    return `${h}h ${m}m`;
  }

  getUserName(log: any): string {
    const u = log.user;

    if (!u) return 'Unknown';

    return `${u.fname || ''} ${u.lname || ''}`.trim() || 'Unknown';
  }

  getSubtaskTitle(log: any): string | null {
    if (!log.task_item_id) return null;

    if (log.project_task_item?.title) {
      return log.project_task_item.title;
    }

    const found = this.subtasks.find(
      (s) => String(s.item_id) === String(log.task_item_id),
    );

    return found?.title ?? `Subtask #${log.task_item_id}`;
  }

  getUserInitials(log: any): string {
    const u = log.user;

    if (!u) return '?';

    return (
      (u.fname || '').charAt(0).toUpperCase() +
        (u.lname || '').charAt(0).toUpperCase() || '?'
    );
  }

  avatarColor(name: string): string {
    const palette = [
      '#6366f1',
      '#8b5cf6',
      '#ec4899',
      '#f59e0b',
      '#10b981',
      '#3b82f6',
      '#ef4444',
      '#14b8a6',
    ];

    let h = 0;

    for (let i = 0; i < name.length; i++) {
      h = name.charCodeAt(i) + ((h << 5) - h);
    }

    return palette[Math.abs(h) % palette.length];
  }

  currentUserInitials(): string {
    return this.currentUserName
      .split(' ')
      .map((w) => w.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  }

  // ── Summary ───────────────────────────────────────────────────────────────

  get totalMinutes(): number {
    return this.timeLogs.reduce((s, l) => s + (l.total_minutes || 0), 0);
  }

  get billableMinutes(): number {
    return this.timeLogs
      .filter((l) => l.is_billable)
      .reduce((s, l) => s + (l.total_minutes || 0), 0);
  }

  get uniqueContributors(): number {
    return new Set(this.timeLogs.map((l) => l.user_id)).size;
  }

  // ── Date Helpers ──────────────────────────────────────────────────────────

  private toDatetimeLocal(iso: string): string {
    if (!iso) return '';

    const d = new Date(iso);

    const pad = (n: number) => String(n).padStart(2, '0');

    return `${d.getFullYear()}-${pad(
      d.getMonth() + 1,
    )}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  relativeTime(dateStr: string): string {
    if (!dateStr) return '';

    const diff = Date.now() - new Date(dateStr).getTime();

    const mins = Math.floor(diff / 60000);

    if (mins < 1) return 'just now';

    if (mins < 60) return `${mins}m ago`;

    const hrs = Math.floor(mins / 60);

    if (hrs < 24) return `${hrs}h ago`;

    const days = Math.floor(hrs / 24);

    return days < 7
      ? `${days}d ago`
      : new Date(dateStr).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
  }
}
